import { useEffect, useRef, useState } from 'react';
import type { HistoricalChart } from '@heat-pwa/types';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';
const CHART_SCHEMA = 'v1';

// Distinct line colors for stations (rep first, then majors).
const LINE_COLORS = ['#e60012', '#ef8200', '#1d8eff'];
const BAR_COLOR = 'rgba(100, 116, 139, 0.45)';
const BAR_BORDER = 'rgba(71, 85, 105, 0.7)';

export function ChartPage({ code, initialYear }: { code: string; initialYear: string }) {
  const [chart, setChart] = useState<HistoricalChart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState(initialYear);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/historical/chart?code=${encodeURIComponent(code)}&v=${CHART_SCHEMA}`)
      .then((r) => {
        if (!r.ok) throw new Error('http ' + r.status);
        return r.json();
      })
      .then((j: HistoricalChart) => {
        if (cancelled) return;
        setChart(j);
        if (!j.years.map(String).includes(year)) setYear(String(j.years[j.years.length - 1]));
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const yearData = chart?.data[year];

  useEffect(() => {
    if (chart && yearData && canvasRef.current) {
      drawChart(canvasRef.current, chart, year);
    }
  }, [chart, yearData, year]);

  const title = chart ? `${chart.name}　${year}年　週別 熱中症 救急搬送人員 と 日別最高気温` : '読み込み中…';

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas || !chart) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${chart.name}_${year}_熱中症グラフ.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="min-h-full bg-slate-50 px-4 py-5 pt-safe pb-safe">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-base font-semibold text-slate-900 sm:text-lg">{title}</h1>

        {error && (
          <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-800">
            データの取得に失敗しました（{error}）。
          </p>
        )}

        {chart && (
          <>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-slate-600">年</span>
              {chart.years.map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => setYear(String(y))}
                  className={
                    'rounded px-2.5 py-1 text-xs ' +
                    (String(y) === year
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300')
                  }
                >
                  {y}
                </button>
              ))}
              <button
                type="button"
                onClick={download}
                className="ml-auto rounded bg-slate-800 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700"
              >
                画像(PNG)を保存
              </button>
            </div>

            <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white p-2">
              <canvas ref={canvasRef} className="block w-full" style={{ maxWidth: '100%' }} />
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
              棒＝週内の熱中症 救急搬送人員（合計、左軸）。折れ線＝観測点ごとの日別最高気温（右軸）。
              観測点は県庁所在地＋人口の多い都市（観測点が無い場合は最寄り観測点）。<br />
              <span className="text-amber-700">※ 救急搬送人員の縦軸の目盛りは、選択した年の最大値に合わせて自動調整されます。年が違うと、棒の高さが同じでも人数は異なります。</span><br />
              出典: 気象庁ホームページ（過去の気象データ）／総務省消防庁（熱中症による救急搬送状況）／地図データ © 国土地理院
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function drawChart(canvas: HTMLCanvasElement, chart: HistoricalChart, year: string): void {
  const yd = chart.data[year];
  if (!yd) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const W = 960;
  const H = 600;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  canvas.style.aspectRatio = `${W} / ${H}`;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  // Title (prefecture + year) — drawn ON the canvas so the saved PNG carries it.
  ctx.fillStyle = '#0f172a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font = 'bold 20px system-ui, sans-serif';
  ctx.fillText(`${chart.name}　${year}年`, W / 2, 28);
  ctx.fillStyle = '#475569';
  ctx.font = '13px system-ui, sans-serif';
  ctx.fillText('週別 熱中症 救急搬送人員 と 日別最高気温', W / 2, 48);

  const margin = { top: 104, right: 64, bottom: 88, left: 60 };
  const plotW = W - margin.left - margin.right;
  const plotH = H - margin.top - margin.bottom;
  const x0 = margin.left;
  const y0 = margin.top;
  const yBot = y0 + plotH;

  // x: 22-week span (day 0 = 5/1 .. day 153 = ~10/1) to align bars and lines.
  const SPAN_DAYS = chart.weeks.length * 7; // 154
  const xAt = (dayIdx: number) => x0 + (dayIdx / (SPAN_DAYS - 1)) * plotW;

  // left axis: transports (max over weeks)
  const maxT = Math.max(10, ...yd.transports);
  const niceMaxT = niceCeil(maxT);
  const yTrans = (v: number) => yBot - (v / niceMaxT) * plotH;

  // right axis: temperature range across stations
  let tmin = Infinity;
  let tmax = -Infinity;
  for (const name of Object.keys(yd.temp)) {
    const arr = yd.temp[name];
    if (!arr) continue;
    for (const v of arr.slice(0, SPAN_DAYS)) {
      if (v == null) continue;
      if (v < tmin) tmin = v;
      if (v > tmax) tmax = v;
    }
  }
  if (!isFinite(tmin)) {
    tmin = 10;
    tmax = 40;
  }
  const tLo = Math.floor(tmin / 5) * 5;
  const tHi = Math.ceil(tmax / 5) * 5;
  const yTemp = (t: number) => yBot - ((t - tLo) / (tHi - tLo)) * plotH;

  // gridlines + left axis (transports)
  ctx.strokeStyle = '#e5e7eb';
  ctx.fillStyle = '#64748b';
  ctx.lineWidth = 1;
  ctx.font = '11px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  const leftTicks = 5;
  for (let i = 0; i <= leftTicks; i++) {
    const v = (niceMaxT / leftTicks) * i;
    const y = yTrans(v);
    ctx.strokeStyle = '#eef2f7';
    ctx.beginPath();
    ctx.moveTo(x0, y);
    ctx.lineTo(x0 + plotW, y);
    ctx.stroke();
    ctx.fillStyle = '#64748b';
    ctx.fillText(String(Math.round(v)), x0 - 8, y);
  }
  // right axis (temp)
  ctx.textAlign = 'left';
  for (let t = tLo; t <= tHi; t += 5) {
    const y = yTemp(t);
    ctx.fillStyle = '#b45309';
    ctx.fillText(`${t}℃`, x0 + plotW + 8, y);
  }

  // weekly transport bars
  const barW = (plotW / chart.weeks.length) * 0.7;
  ctx.fillStyle = BAR_COLOR;
  ctx.strokeStyle = BAR_BORDER;
  for (let w = 0; w < chart.weeks.length; w++) {
    const v = yd.transports[w] ?? 0;
    const cx = xAt(w * 7 + 3);
    const top = yTrans(v);
    ctx.fillRect(cx - barW / 2, top, barW, yBot - top);
    ctx.strokeRect(cx - barW / 2, top, barW, yBot - top);
  }

  // daily temp lines per station
  const stationNames = chart.stations.map((s) => s.name);
  stationNames.forEach((name, idx) => {
    const series = yd.temp[name];
    if (!series) return;
    ctx.strokeStyle = LINE_COLORS[idx % LINE_COLORS.length]!;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    let pen = false;
    for (let i = 0; i < SPAN_DAYS && i < series.length; i++) {
      const v = series[i];
      if (v == null) {
        pen = false;
        continue;
      }
      const px = xAt(i);
      const py = yTemp(v);
      if (!pen) {
        ctx.moveTo(px, py);
        pen = true;
      } else {
        ctx.lineTo(px, py);
      }
    }
    ctx.stroke();
  });

  // x-axis month labels
  ctx.fillStyle = '#475569';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  for (let w = 0; w < chart.weeks.length; w++) {
    const wk = chart.weeks[w]!;
    if (wk.startDay <= 7) {
      // first week of a month → label the month
      const cx = xAt(w * 7 + 3);
      ctx.fillText(`${wk.startMonth}月`, cx, yBot + 8);
    }
  }

  // caution note about the auto-scaling transports axis (also in the PNG)
  ctx.fillStyle = '#b45309';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = '11px system-ui, sans-serif';
  ctx.fillText(
    '※ 救急搬送人員（左軸）の目盛りは選択した年の最大値に合わせて自動調整されます。年が違うと、棒の高さが同じでも人数は異なります。',
    x0 - 48,
    yBot + 34,
  );

  // axis frame
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x0, yBot);
  ctx.lineTo(x0 + plotW, yBot);
  ctx.stroke();

  // axis titles
  ctx.fillStyle = '#475569';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.font = '12px system-ui, sans-serif';
  ctx.fillText('熱中症搬送人員（人/週）', x0 - 48, y0 - 18);
  ctx.textAlign = 'right';
  ctx.fillText('最高気温（℃）', x0 + plotW + 56, y0 - 18);

  // legend (below the title)
  const legend: Array<{ label: string; color: string; bar?: boolean }> = [
    { label: '熱中症搬送(週)', color: BAR_COLOR, bar: true },
    ...stationNames.map((n, i) => ({ label: n, color: LINE_COLORS[i % LINE_COLORS.length]! })),
  ];
  ctx.font = '12px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  const itemW = 110;
  const totalW = legend.length * itemW;
  let lx = x0 + (plotW - totalW) / 2;
  const ly = 74;
  for (const it of legend) {
    if (it.bar) {
      ctx.fillStyle = it.color;
      ctx.fillRect(lx, ly - 6, 16, 12);
      ctx.strokeStyle = BAR_BORDER;
      ctx.strokeRect(lx, ly - 6, 16, 12);
    } else {
      ctx.strokeStyle = it.color;
      ctx.lineWidth = 2.4;
      ctx.beginPath();
      ctx.moveTo(lx, ly);
      ctx.lineTo(lx + 18, ly);
      ctx.stroke();
    }
    ctx.fillStyle = '#334155';
    ctx.fillText(it.label, lx + 22, ly);
    lx += itemW;
  }
}

function niceCeil(v: number): number {
  if (v <= 0) return 10;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}
